import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, FileText, Search, Scale, Gavel, MessageSquare, Shield, Mail, GitCompare, BookOpen, Bot, Laptop, Building, FileEdit, Mic } from 'lucide-react';
import MatrixBackground from '@/MatrixBackground';
import MarketingHeader from '@/components/MarketingHeader';
import usePageSEO from '@/hooks/usePageSEO';

const caseStudies = [
  {
    slug: 'contract-review-commercial-law',
    category: 'Contract Intelligence',
    title: 'How a Commercial Law Firm Reviewed 200+ Contracts in One Week',
    excerpt: 'A mid-size commercial law firm used Lawra AI to accelerate contract review during a major portfolio acquisition, reducing review time by 78% while catching critical risk clauses.',
    icon: FileText,
    stats: { metric: '78%', label: 'Time Saved' },
    accentColor: '#D4AF37',
  },
  {
    slug: 'due-diligence-ma-transaction',
    category: 'Due Diligence',
    title: 'Automating Due Diligence for a Multi-Million Pound M&A Deal',
    excerpt: 'A corporate law team processed hundreds of transaction documents with Lawra AI, identifying material risks and compliance gaps that manual review had missed.',
    icon: Search,
    stats: { metric: '340+', label: 'Documents Processed' },
    accentColor: '#00D4FF',
  },
  {
    slug: 'litigation-prep-personal-injury',
    category: 'Litigation Support',
    title: 'Streamlining Deposition Prep for a Complex Personal Injury Case',
    excerpt: 'A personal injury practice used Lawra AI to prepare comprehensive deposition questions and witness analysis, turning weeks of preparation into days.',
    icon: Gavel,
    stats: { metric: '65%', label: 'Prep Time Reduced' },
    accentColor: '#FF6B35',
  },
  {
    slug: 'legal-research-criminal-defence',
    category: 'Legal Research',
    title: 'Accelerating Case Law Research for a Criminal Defence Team',
    excerpt: 'A criminal defence solicitor leveraged Lawra AI to rapidly research precedents and summarise complex case law, building stronger arguments in less time.',
    icon: Scale,
    stats: { metric: '4x', label: 'Faster Research' },
    accentColor: '#8B5CF6',
  },
  {
    slug: 'document-generation-family-law',
    category: 'Document Generation',
    title: 'Automating Standard Documents for a Family Law Practice',
    excerpt: 'A family law firm used Lawra AI to generate tailored legal documents, consent orders, and correspondence, freeing solicitors to focus on client relationships.',
    icon: MessageSquare,
    stats: { metric: '90%', label: 'Drafting Time Cut' },
    accentColor: '#10B981',
  },
  {
    slug: 'debt-recovery-automation',
    category: 'Debt Recovery',
    title: 'Scaling Debt Recovery Operations with AI-Powered Letters',
    excerpt: 'A collections firm integrated Lawra AI to generate compliant, personalised debt recovery letters at scale, improving recovery rates and reducing manual workload.',
    icon: Shield,
    stats: { metric: '3x', label: 'Recovery Volume' },
    accentColor: '#F59E0B',
  },
  {
    slug: 'email-response-employment-law',
    category: 'Employment Law',
    title: 'How an Employment Law Firm Halved Response Times on Tribunal Correspondence',
    excerpt: 'An employment law practice used Lawra AI\'s Email Response Assistant to draft tribunal correspondence and client updates, cutting response turnaround from days to hours.',
    icon: Mail,
    stats: { metric: '52%', label: 'Faster Responses' },
    accentColor: '#EC4899',
  },
  {
    slug: 'document-comparison-property-law',
    category: 'Property Law',
    title: 'Tracking Lease Variations Across a 300-Unit Residential Portfolio',
    excerpt: 'A property law team used Lawra AI\'s Document Comparison tool to identify critical deviations across hundreds of lease variations, preventing costly oversights in a major portfolio restructure.',
    icon: GitCompare,
    stats: { metric: '300+', label: 'Leases Compared' },
    accentColor: '#06B6D4',
  },
  {
    slug: 'legal-summarisation-insurance-law',
    category: 'Insurance Law',
    title: 'Summarising Complex Policy Wordings for a Lloyd\'s Syndicate Dispute',
    excerpt: 'An insurance litigation team used Lawra AI\'s Legal Summarisation tool to distil hundreds of pages of policy wordings and endorsements into actionable summaries for counsel.',
    icon: BookOpen,
    stats: { metric: '85%', label: 'Review Time Cut' },
    accentColor: '#7C3AED',
  },
  {
    slug: 'ai-chatbot-immigration-law',
    category: 'Immigration Law',
    title: 'Accelerating Visa Application Research for a Multi-National Practice',
    excerpt: 'An immigration law firm used Ask Lawra to quickly research visa categories, eligibility requirements, and policy changes, dramatically reducing the time spent on initial client consultations.',
    icon: Bot,
    stats: { metric: '40%', label: 'Consultation Time Saved' },
    accentColor: '#14B8A6',
  },
  {
    slug: 'contract-review-technology-ip',
    category: 'Technology & IP',
    title: 'Reviewing SaaS Agreements and IP Licences for a Tech Startup Portfolio',
    excerpt: 'A technology-focused law firm used Lawra AI to review SaaS subscriptions, IP licence agreements, and data processing addenda across a venture capital portfolio of 40+ startups.',
    icon: Laptop,
    stats: { metric: '40+', label: 'Companies Reviewed' },
    accentColor: '#3B82F6',
  },
  {
    slug: 'regulatory-compliance-financial-services',
    category: 'Financial Services',
    title: 'Ensuring FCA Compliance Across Client Documentation for a Wealth Manager',
    excerpt: 'A financial services law team used Lawra AI to audit client-facing documents for FCA compliance, identifying regulatory gaps and generating compliant replacement drafts at scale.',
    icon: Building,
    stats: { metric: '100%', label: 'Compliance Achieved' },
    accentColor: '#EF4444',
  },
  {
    slug: 'inline-review-lease-negotiation',
    category: 'Inline Review',
    title: 'Annotating and Redlining a 90-Page Commercial Lease in Under 2 Hours',
    excerpt: 'A property solicitor used Lawra AI\'s Inline Review tool to annotate a complex commercial lease, producing a fully marked-up document with risk commentary and suggested amendments ready for negotiation.',
    icon: FileEdit,
    stats: { metric: '2hrs', label: 'Full Review Time' },
    accentColor: '#F97316',
  },
  {
    slug: 'ask-lawra-client-consultations',
    category: 'Ask Lawra AI',
    title: 'Cutting Initial Client Consultation Research Time by 60% with AI Chat',
    excerpt: 'A high-street solicitor used Ask Lawra AI during client meetings to instantly research procedural questions, check limitation periods, and verify statutory requirements — replacing hours of post-meeting research.',
    icon: Bot,
    stats: { metric: '60%', label: 'Research Time Cut' },
    accentColor: '#0EA5E9',
  },
  {
    slug: 'notetaker-client-instructions',
    category: 'Notetaker',
    title: 'Capturing and Structuring Client Instructions from 50+ Weekly Calls',
    excerpt: 'A busy conveyancing practice used Lawra AI\'s Notetaker to record client calls and produce clean, structured attendance notes — eliminating hours of daily manual note-taking.',
    icon: Mic,
    stats: { metric: '50+', label: 'Calls Per Week' },
    accentColor: '#A855F7',
  },
  {
    slug: 'document-generation-corporate-restructure',
    category: 'Document Generation',
    title: 'Generating 80+ Board Resolutions and Minutes for a Group Restructure',
    excerpt: 'A corporate law team used Lawra AI to draft board resolutions, shareholder minutes, and Companies House filings across a 15-entity group restructure — completing in days what would have taken weeks.',
    icon: FileText,
    stats: { metric: '80+', label: 'Documents Generated' },
    accentColor: '#22C55E',
  },
  {
    slug: 'contract-review-nhs-procurement',
    category: 'Contract Review',
    title: 'Reviewing NHS Standard Contract Terms Across 25 Service Agreements',
    excerpt: 'A healthcare law team used Lawra AI to review NHS standard contract variations, identifying non-compliant service specifications and liability caps that required renegotiation before the funding deadline.',
    icon: Shield,
    stats: { metric: '25', label: 'Contracts Reviewed' },
    accentColor: '#DC2626',
  },
];

const UseCases = () => {
  const navigate = useNavigate();

  const useCasesStructuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Lawra AI Use Cases - How Lawyers Win with AI Legal Tools",
    "description": "Explore real case studies showing how law firms use Lawra AI for contract review, due diligence, litigation prep, legal research, document generation and debt recovery.",
    "url": "https://lawra-ai.com/use-cases",
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": caseStudies.map((cs, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "name": cs.title,
        "url": `https://lawra-ai.com/use-cases/${cs.slug}`,
      })),
    },
  };

  usePageSEO({
    title: 'Use Cases - How Law Firms Save Time with AI Legal Tools',
    description: 'Explore real case studies showing how law firms use Lawra AI to cut contract review time by 78%, automate due diligence, accelerate legal research 4x, and generate documents 90% faster.',
    path: '/use-cases',
    structuredData: useCasesStructuredData,
  });

  return (
    <div className="landing-page">
      <MatrixBackground />
      <MarketingHeader />

      {/* Hero */}
      <section className="uc-hero" data-testid="use-cases-hero">
        <div className="uc-hero-inner">
          <p className="uc-hero-label">Use Cases</p>
          <h1 className="uc-hero-title">See How Lawyers Win with Lawra AI</h1>
          <p className="uc-hero-subtitle">
            Real scenarios showing how legal teams save time, reduce risk, and deliver better outcomes using AI-powered tools.
          </p>
        </div>
      </section>

      {/* Case Study Grid */}
      <section className="uc-grid-section" data-testid="use-cases-grid">
        <div className="uc-grid">
          {caseStudies.map((cs, i) => {
            const Icon = cs.icon;
            return (
              <article
                key={cs.slug}
                className="uc-card"
                data-testid={`use-case-card-${cs.slug}`}
                onClick={() => navigate(`/use-cases/${cs.slug}`)}
                style={{ '--accent': cs.accentColor, animationDelay: `${i * 0.08}s` }}
              >
                <div className="uc-card-top">
                  <div className="uc-card-icon">
                    <Icon size={24} />
                  </div>
                  <span className="uc-card-category">{cs.category}</span>
                </div>

                <h2 className="uc-card-title">{cs.title}</h2>
                <p className="uc-card-excerpt">{cs.excerpt}</p>

                <div className="uc-card-bottom">
                  <div className="uc-card-stat">
                    <span className="uc-card-stat-metric">{cs.stats.metric}</span>
                    <span className="uc-card-stat-label">{cs.stats.label}</span>
                  </div>
                  <span className="uc-card-link">
                    Read Case Study <ArrowRight size={16} />
                  </span>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section" data-testid="use-cases-cta">
        <div className="cta-content">
          <h2>Ready to See These Results for Yourself?</h2>
          <p>Start your free trial and experience how Lawra AI transforms legal work</p>
          <Button
            data-testid="use-cases-cta-btn"
            className="try-now-btn"
            size="lg"
            onClick={async () => {
              try {
                const { auth } = await import('../firebase');
                if (auth.currentUser) {
                  navigate('/dashboard');
                } else {
                  navigate('/onboarding');
                }
              } catch {
                navigate('/onboarding');
              }
            }}
          >
            Start Your Free Trial
          </Button>
          <p className="cta-subtext">No credit card required</p>
        </div>
      </section>
    </div>
  );
};

export default UseCases;
